const OFFSET_PATTERN = /^([+-])(\d{1,2})(?::?(\d{2}))?$/

export function normalizeUtcOffset(raw: string): string {
  const cleaned = raw.trim().toUpperCase().replace(/^GMT/, "").replace(/^UTC/, "")
  if (!cleaned || cleaned === "Z") {
    return "+00:00"
  }

  const match = cleaned.match(OFFSET_PATTERN)
  if (!match) {
    return "+00:00"
  }

  const [, sign, hours, minutes = "0"] = match
  const normalizedHours = hours.padStart(2, "0")
  const normalizedMinutes = minutes.padStart(2, "0")
  return `${sign}${normalizedHours}:${normalizedMinutes}`
}

export function computeUtcOffsetForTimezone(date: string, time: string, timezone: string): string {
  try {
    const ref = new Date(`${date}T${time || "00:00"}:00Z`)
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    }).formatToParts(ref)
    const tzName = parts.find((part) => part.type === "timeZoneName")?.value ?? "GMT"
    return normalizeUtcOffset(tzName)
  } catch {
    return "+00:00"
  }
}

export function formatUtcOffsetLabel(offset: string): string {
  const normalized = normalizeUtcOffset(offset)
  return normalized === "+00:00" ? "UTC" : `UTC${normalized}`
}

function offsetToMinutes(offset: string): number {
  const normalized = normalizeUtcOffset(offset)
  const match = normalized.match(/^([+-])(\d{2}):(\d{2})$/)
  if (!match) return 0
  const sign = match[1] === "+" ? 1 : -1
  const hours = Number(match[2])
  const minutes = Number(match[3])
  return sign * (hours * 60 + minutes)
}

function minutesToOffset(minutes: number): string {
  const sign = minutes >= 0 ? "+" : "-"
  const absolute = Math.abs(minutes)
  const hours = Math.floor(absolute / 60)
  const mins = absolute % 60
  return `${sign}${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`
}

export function buildUtcOffsetOptions(stepMinutes = 15): string[] {
  const options: string[] = []
  for (let minutes = -12 * 60; minutes <= 14 * 60; minutes += stepMinutes) {
    options.push(minutesToOffset(minutes))
  }
  return options
}
